import { S } from './strings';

const MINUTE_MS = 60_000;
const DAY_MS = 86_400_000;

export const FALLBACK_HISTORY_START_MS = Date.UTC(2024, 4, 1);
export const LIVE_TOLERANCE_MS = 2 * MINUTE_MS;

export type RangeKey = '1d' | '1w' | '1m' | 'last' | '3m' | '1y' | 'all';

export const DEFAULT_RANGE: RangeKey = '1y';

export const QUICK_RANGES: ReadonlyArray<{ id: RangeKey; label: string }> = [
    { id: '1d', label: S.r_1d },
    { id: '1w', label: S.r_1w },
    { id: '1m', label: S.r_1m },
    { id: 'last', label: S.r_last },
    { id: '3m', label: S.r_3m },
    { id: '1y', label: S.r_1y },
    { id: 'all', label: S.r_all },
];

const SPANS: Partial<Record<RangeKey, number>> = {
    '1d': DAY_MS,
    '1w': 7 * DAY_MS,
    '1m': 30 * DAY_MS,
    '1y': 365 * DAY_MS,
};

export interface RangeContext {
    nowMs: number;
    historyStartMs: number;
    meetingMs: readonly number[] | null;
}

export function isRangeKey(value: string | null): value is RangeKey {
    return QUICK_RANGES.some(q => q.id === value);
}

export function minuteNow(ms: number = Date.now()): number {
    return Math.floor(ms / MINUTE_MS) * MINUTE_MS;
}

function clampStart(start: number, ctx: RangeContext): [Date, Date] | null {
    const s = Math.max(start, ctx.historyStartMs);
    return s < ctx.nowMs ? [new Date(s), new Date(ctx.nowMs)] : null;
}

export function rangeFor(
    key: RangeKey,
    ctx: RangeContext
): [Date, Date] | null {
    const span = SPANS[key];
    if (span !== undefined) return clampStart(ctx.nowMs - span, ctx);
    if (key === 'all') return clampStart(ctx.historyStartMs, ctx);
    if (!ctx.meetingMs) return null;
    const past = ctx.meetingMs.filter(t => t <= ctx.nowMs);
    if (key === 'last' && past.length >= 1) {
        return clampStart(past[past.length - 1] - 21 * DAY_MS, ctx);
    }
    if (key === '3m' && past.length >= 3) {
        return clampStart(past[past.length - 3] - 7 * DAY_MS, ctx);
    }
    return null;
}

export function focusRange(
    decisionMs: number,
    ctx: RangeContext
): [Date, Date] | null {
    if (decisionMs > ctx.nowMs) return clampStart(ctx.nowMs - 60 * DAY_MS, ctx);
    const end = Math.min(ctx.nowMs, decisionMs + 7 * DAY_MS);
    const start = Math.max(ctx.historyStartMs, decisionMs - 30 * DAY_MS);
    return start < end ? [new Date(start), new Date(end)] : null;
}

export function parseAbsolute(
    from: string | null,
    to: string | null,
    ctx: RangeContext
): [Date, Date] | null {
    if (!from || !to) return null;
    const a = new Date(from).getTime();
    const b = new Date(to).getTime();
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    const start = Math.max(a, ctx.historyStartMs);
    const end = Math.min(b, ctx.nowMs);
    return start < end ? [new Date(start), new Date(end)] : null;
}

export function isLiveRange(
    range: readonly [Date, Date],
    nowMs: number
): boolean {
    return nowMs - range[1].getTime() <= LIVE_TOLERANCE_MS;
}
